import { Injectable, signal, inject, Injector } from '@angular/core';
import { PromotionModel } from "../models/promotion.model";
import { BehaviorSubject, catchError, map, Observable, throwError } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { ConfigService } from "./config.service";
import { ResponseModel } from "../models/response.model";
import { ToastrService } from "ngx-toastr";
import { TranslateService } from "@ngx-translate/core";
import { ErrorMapperService } from "./error-mapper.service";
import { AuthService } from "./auth.service";
import { AppErrorModel } from '../models/appError.model';

@Injectable({
  providedIn: 'root'
})
export class PromotionService {

  private injector = inject(Injector);

  staticPromotions: PromotionModel[] = this.configs.publicPromotions;

  private promosSignal = signal<PromotionModel[] | null>(null);
  public promos = this.promosSignal.asReadonly();

  //active or claimed promo
  private currentPromoSignal = signal<PromotionModel | null>(null);
  currentPromo = this.currentPromoSignal.asReadonly();



  constructor(
    private http: HttpClient,
    private configs: ConfigService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private errorMapper: ErrorMapperService
  ) { }


  //get and store user-promos in signal + handle errors  
  fetchUserPromos() {
    this.getUserPromos().subscribe({
      next: (promos) => {
        this.promosSignal.set(promos);
        // update activePromoSignal if there's an active/claimed promo to sync immediately
        const activeOrClaimed = promos.find(p => p.state !== 'eligible') || null;
        this.updateCurrentPromo(activeOrClaimed);
      },
      error: (err: AppErrorModel) => {
        // console.error('Failed to load promos:', err)
        this.promosSignal.set([]);

        switch (err.type) {
          case 'SESSION':
            this.injector.get(AuthService).resetUserSession();
            this.toastr.error(this.translate.instant('LOGIN.TOASTR_ERROR_LOGGEDOUT_MESSAGE'), this.translate.instant('LOGIN.TOASTR_ERROR_LOGGEDOUT_H1'), { toastClass: 'ngx-toastr lobbyless-toast' });
            break;
          case "SYSTEM": //SYSTEM/NETWORK type has a message to translate
            this.toastr.error(this.translate.instant(err.message ?? 'ACCOUNT.PROMO.TOASTR_FAILED_TIP'), this.translate.instant('ACCOUNT.PROMO.TOASTR_FAILED_H1'), { toastClass: 'ngx-toastr lobbyless-toast' })
            break;
          default: //case 'USER' has message but we dont care, no FIELD case
            this.toastr.error(this.translate.instant('ACCOUNT.PROMO.TOASTR_FAILED_TIP'), this.translate.instant('ACCOUNT.PROMO.TOASTR_FAILED_H1'), { toastClass: 'ngx-toastr lobbyless-toast' })
            break;
        }
      }
    });
  }


  //get promotions for user
  getUserPromos(): Observable<PromotionModel[]> {
    //"method":"user.promotion.list" is deprecated since 2025 february
    return this.http.post<ResponseModel>(`${this.configs.apiUrl}`, { "jsonrpc": "2.0", "id": this.configs.casinoId, "method": "promotion.list", "params": {} }).pipe(
      map((response: ResponseModel) => {
        if ('result' in response && 'promotions' in response.result) {
          return response.result.promotions as PromotionModel[];
        }
        throw response;
      }),
      catchError((error) => throwError(() => this.errorMapper.mapAPIerrors(error)))
    )
  }

  //to claim a promotion (status eligible -> active)
  claimPromotion(promoId: string): Observable<{ result: {} }> {
    return this.http.post<ResponseModel>(`${this.configs.apiUrl}`, {
      "jsonrpc": "2.0", "id": this.configs.casinoId, "method": "promotion.claim", "params": {
        id: promoId
      }
    }).pipe(
      map((response: ResponseModel) => {
        if ('result' in response) {
          return response;
        }
        throw response;
      }),
      catchError((error) => throwError(() => this.errorMapper.mapAPIerrors(error)))
    )
  }


  //to unclaim a claimed promotion
  unclaimPromotion(): Observable<{ result: {} }> {
    return this.http.post<ResponseModel>(`${this.configs.apiUrl}`, { "jsonrpc": "2.0", "id": this.configs.casinoId, "method": "promotion.unclaim", "params": {} }).pipe(
      map((response: ResponseModel) => {
        if ('result' in response) {
          return response;
        }
        throw response
      }),
      catchError((error) => throwError(() => this.errorMapper.mapAPIerrors(error)))
    )
  }

  //to complete an active promotion (status active -> ???)
  //removed because it is deprecated + not working (couldn't complete but had success message) => maybe we will have it once
  // completePromotion(): Observable<{ result: {} }>{
  //   return this.http.post<ResponseModel>(`${this.configs.apiUrl}`, {"jsonrpc":"2.0","id": this.configs.casinoId,"method":"promotion.complete","params":{}}).pipe(
  //     map((response: ResponseModel)=> {
  //       if ('result' in response){
  //         return response;
  //       }
  //       if ('error' in response) {
  //         throw response.error;
  //       }
  //       // If neither 'error' nor valid 'result' exists, throw an error
  //       throw new Error('Invalid response structure');
  //     }),
  //     catchError((error) => {
  //       return throwError(()=> error)
  //     })
  //   )
  // }

  //to apply a promotion code (must be uppercase and alphanumeric)
  applyPromoCode(code: string): Observable<{ result: {} }> {
    return this.http.post<ResponseModel>(`${this.configs.apiUrl}`, {
      "jsonrpc": "2.0", "id": this.configs.casinoId, "method": "promotion.apply", "params": { code: code }
    }).pipe(
      map((response: ResponseModel) => {
        if ('result' in response) {
          return response;
        }
        throw response;
      }),
      catchError((error) => throwError(() => this.errorMapper.mapAPIerrors(error)))
    )
  }

  updateCurrentPromo(promo: PromotionModel | null): void {
    this.currentPromoSignal.set(promo);
  }

  updatePromos(promos: PromotionModel[] | null): void {
    this.promosSignal.set(promos);
  }



}

